import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { useNavigate, useLocation } from 'react-router-dom';
import parse from 'html-react-parser';
import html2PDF from 'jspdf-html2canvas';
import { useErrorHandler } from 'react-error-boundary';

//icons
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import CloseIcon from '@mui/icons-material/Close';
import {
    LocationIcon,
    PersonIcon,
    WhatsappIcon,
    LinkedinIcon,
    GmailIcon,
    ExperienceIcon,
    MessengerIcon,
} from '../../components/icons/icons';

import logo from '../../assets/images/CentelonLogo.png';
import Loader from '../../components/loader/Loader';
import TriangleBg from '../../assets/images/TriangleBg.svg';
import { makeStyles } from '@mui/styles';

import { useDispatch, useSelector } from 'react-redux';
import { fullJobDetails } from '../../redux/actions/JobDetailsAction';
import { decrypt } from '../../util/Security';

const useStyles = makeStyles((theme) => ({
    container: {
        position: 'relative',
    },
    backdrop: {
        background: 'linear-gradient(180deg, #6BF98D 0%, #16C7FF 100%)',
        height: '45vh',
    },
    pageContents: {
        backgroundColor: 'white',
        width: '75%',
        position: 'absolute',
        top: '20vh',
        left: '50%',
        transform: 'translate(-50%, 0%)',
        borderRadius: 4,
        padding: 16,
        paddingTop: 24,
        textAlign: 'left',
        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 5px 0px, rgba(0, 0, 0, 0.1) 0px 0px 1px 0px',
        marginBottom: 40,
    },
    subTitle: {
        paddingRight: 20,
    },
    modal: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'white',
        boxShadow: 24,
        padding: 24,
        paddingTop: 50,
        borderRadius: 4,
    },
    closeIcon: {
        position: 'absolute !important',
        right: 5,
        top: 5,
    },
    socialSection: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        fontSize: '2.5em',
        paddingTop: 25,
        paddingBottom: 25,
    },
    smButtom: {
        cursor: 'pointer',
    },
    copyIcon: {
        borderRadius: 4,
        backgroundColor: 'lightgrey',
        fontSize: '0.875em',
        minWidth: 70,
    },
}));

const JobDetails = ({ mobileView, setHeight }) => {
    const navigate = useNavigate();
    const errorHandler = useErrorHandler();
    const dispatch = useDispatch();
    const location = useLocation();
    const classes = useStyles();
    const contentsRef = useRef(null);
    const [containerHeight, setContainerHeight] = useState(400);
    const [open, setOpen] = useState(() => false);
    const [copyButton, setCopyButton] = useState(() => 'Copy');
    const { jobDetails } = useSelector((state) => state?.jobDetailsState);
    const careerLink = 'https://centelon.com/company/careers/';
    useEffect(() => {
        let loggedIn = localStorage.getItem('login');
        let data = decrypt(loggedIn);
        data.jobId = location?.state?.jobId;
        dispatch(fullJobDetails(data));

        return () => {
            dispatch(fullJobDetails(null));
        };
    }, []);

    useEffect(() => {
        if (null !== contentsRef.current) {
            let offset = contentsRef.current?.offsetTop;
            let height = contentsRef.current?.clientHeight;
            let newHeight = offset + height + 50;
            setContainerHeight(newHeight);
        }
    }, [contentsRef.current]);

    useEffect(() => {
        setTimeout(() => {
            let documentHeight = document.getElementsByTagName('body')[0].clientHeight;

            if (documentHeight > containerHeight) setHeight('100vh');
            else setHeight(documentHeight);
        }, 100);
    }, [containerHeight]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleShareClick = (action) => {
        try {
            if (action === 'whatsapp')
                window.open(`https://web.whatsapp.com/send?text=${careerLink}`);
            else if (action === 'linkedin')
                window.open(
                    'https://www.linkedin.com/sharing/share-offsite/?url=https://www.linkedin.com/company/centelon-ltd/jobs/'
                );
            else if (action === 'gmail')
                window.open(
                    `https://mail.google.com/mail/?view=cm&fs=1&to=&su=Job%20Opening%20with%20Centelon&body=${careerLink}`
                );
            else if (action === 'messenger') {
                window.open(
                    `fb-messenger://share?link=${encodeURIComponent(
                        careerLink
                    )} &app_id=${encodeURIComponent(123456789)}`
                );
            } else if (action === 'copy') {
                navigator.clipboard.writeText(careerLink);
                setCopyButton('Copied!');
                setTimeout(() => {
                    setCopyButton('Copy');
                }, 500);
            }
        } catch (error) {
            errorHandler(error);
        }
    };

    const exportPDF = () => {
        try {
            document.getElementById('back').style.display = 'none';
            document.getElementById('logo').style.display = 'block';
            let page = contentsRef.current;

            html2PDF(page, {
                jsPDF: {
                    unit: 'in',
                    format: 'a4',
                },
                margin: {
                    top: 0.5,
                    bottom: 0.5,
                    left: 0.5,
                    right: 0.5,
                },
                output: 'Centelon Job Opening.pdf',
                init: function () {},
                success: function (pdf) {
                    pdf.save(this.output);
                },
            });
            document.getElementById('back').style.display = 'inline-block';
            document.getElementById('logo').style.display = 'none';
        } catch (error) {
            errorHandler(error);
        }
    };

    return (
        <Box className={classes.container} height={containerHeight}>
            <Box className={classes.backdrop}>
                <Box sx={{ background: `url(${TriangleBg})`, height: '100%' }}></Box>
            </Box>
            <Box id='pageContents' className={classes.pageContents}>
                {jobDetails ? (
                    <>
                        <Box ref={contentsRef}>
                            <Box id='logo' py={2} display='none'>
                                <img src={logo} height={60} />
                            </Box>
                            <Typography
                                variant={mobileView ? 'h5' : 'h4'}
                                component='h1'
                                fontWeight='bold'>
                                <IconButton id='back' size='large' onClick={() => navigate(-1)}>
                                    <KeyboardBackspaceIcon
                                        color='inherit'
                                        fontSize='inherit'
                                        fontWeight='inherit'
                                    />
                                </IconButton>
                                {jobDetails?.position_name}
                            </Typography>
                            <Box pl={6.5} mt={1}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm='auto'>
                                        <Typography sx={{ mr: 0.8 }} component='span'>
                                            <LocationIcon fontSize='inherit' />
                                            <Typography
                                                component='span'
                                                variant='body'
                                                sx={{ ml: 0.8, mt: -1 }}>
                                                {jobDetails?.position_location?.join(', ') || 'Any'}
                                            </Typography>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm='auto'>
                                        <Typography sx={{ mr: 0.8 }} component='span'>
                                            <PersonIcon fontSize='inherit' />
                                            <Typography
                                                component='span'
                                                variant='body'
                                                sx={{ ml: 0.8, mt: -1 }}>
                                                {`${jobDetails?.no_of_vacancy}  Vacancy`}
                                            </Typography>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm='auto'>
                                        <Typography sx={{ mr: 0.8 }} component='span'>
                                            <ExperienceIcon fontSize='inherit' />
                                            <Typography
                                                component='span'
                                                variant='body'
                                                sx={{ ml: 0.8, mt: -1 }}>
                                                {jobDetails?.work_experience}
                                            </Typography>
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Box pt={8}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            {jobDetails && parse(jobDetails?.responsibilities)}
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>
                        </Box>
                        <Box textAlign='center' py={2}>
                            <Button
                                variant='contained'
                                onClick={() => setOpen(true)}
                                // onClick={() => exportPDF()}
                            >
                                Share Job
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Box sx={{ height: '30vh' }}>
                        <Loader />
                    </Box>
                )}
            </Box>

            {/* Modal Popup */}
            <Modal open={open} onClose={handleClose}>
                <Box className={classes.modal} width={mobileView ? 250 : 500}>
                    <IconButton className={classes.closeIcon} onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                    <Typography variant='h5' component='h2' textAlign='center'>
                        Share this job with your community
                    </Typography>
                    <Box width='80%' mx='auto' mb={4}>
                        <Box className={classes.socialSection}>
                            <Grid container spacing={2}>
                                <Grid item xs={4} sm={4}>
                                    <WhatsappIcon
                                        className={classes.smButtom}
                                        onClick={() => handleShareClick('whatsapp')}
                                    />
                                </Grid>
                                <Grid item xs={4} sm={4}>
                                    <LinkedinIcon
                                        className={classes.smButtom}
                                        onClick={() => handleShareClick('linkedin')}
                                    />
                                </Grid>
                                <Grid item xs={4} sm={4}>
                                    <GmailIcon
                                        className={classes.smButtom}
                                        onClick={() => handleShareClick('gmail')}
                                    />
                                </Grid>
                                {/* <Grid item xs={6} sm={3}>
                                    <MessengerIcon
                                        className={classes.smButtom}
                                        onClick={() => handleShareClick('messenger')}
                                    />
                                </Grid> */}
                            </Grid>
                        </Box>
                        <Typography textAlign='center' sx={{ m: 2 }}>
                            or copy link
                        </Typography>
                        <TextField
                            value={careerLink}
                            variant='outlined'
                            size='small'
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        <IconButton
                                            onClick={() => handleShareClick('copy')}
                                            edge='end'
                                            size='small'
                                            className={classes.copyIcon}>
                                            {copyButton}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            fullWidth={true}
                            disabled
                        />
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default JobDetails;
